import { appendPunctuation } from 'lib/riskbase';
import React from 'react';
import {
  AccessibilityProps,
  GestureResponderEvent,
  Platform,
  Pressable as RNPressable,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';

export type PressableProps = {
  onPress?: (event: GestureResponderEvent) => void;
  onLongPress?: () => void;
  onPressIn?: (event: GestureResponderEvent) => void;
  onPressOut?: (event: GestureResponderEvent) => void;
  pressedStyle?: StyleProp<ViewStyle>;
  hoveredStyle?: StyleProp<ViewStyle>;
  onMoveShouldSetResponder?: (event: GestureResponderEvent) => boolean;
  onResponderGrant?: (event: GestureResponderEvent) => void;
  onResponderMove?: (event: GestureResponderEvent) => void;
  onResponderRelease?: (event: GestureResponderEvent) => void;
  onStartShouldSetResponder?: (event: GestureResponderEvent) => boolean;
  onResponderTerminationRequest?: (event: GestureResponderEvent) => boolean;
  onResponderTerminate?: (event: GestureResponderEvent) => void;
  onMouseEnter?: (event: MouseEvent) => void;
  onMouseLeave?: (event: MouseEvent) => void;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  pointerEvents?: ViewStyle['pointerEvents'];
  accessible?: boolean;
  focusOnRender?: boolean;
  accessibilityLabel?: AccessibilityProps['accessibilityLabel'];
  accessibilityHint?: AccessibilityProps['accessibilityHint'];
  onContextPress?: (event: MouseEvent) => void;
  children?: React.ReactNode;
  focusable?: boolean;
  menuElement?: React.ReactElement;
  tabIndex?: 0 | -1;
};

type RNPressableProps = React.ComponentProps<typeof RNPressable> & {
  onMouseEnter?: (event: MouseEvent) => void;
  onMouseLeave?: (event: MouseEvent) => void;
};

const RNPressableTyped = RNPressable as React.ComponentType<RNPressableProps>;

export const Pressable: React.FC<PressableProps> = ({
  accessible,
  children,
  disabled,
  focusOnRender,
  onLongPress,
  onPress,
  onPressIn,
  onPressOut,
  pointerEvents,
  pressedStyle,
  hoveredStyle,
  style,
  accessibilityLabel,
  accessibilityHint,
  onMoveShouldSetResponder,
  onResponderGrant,
  onResponderMove,
  onResponderRelease,
  onStartShouldSetResponder,
  onResponderTerminationRequest,
  onResponderTerminate,
  onMouseEnter,
  onMouseLeave,
  onContextPress,
  focusable = true,
  menuElement,
  tabIndex,
}) => {
  // Stop accidental double button presses
  const pressedTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>(null);
  const focusTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>(null);
  const hasFocusedOnRef = React.useRef(false);
  const pressableRef = React.useRef<View>(null);
  const handlePress = React.useCallback(
    (event: GestureResponderEvent) => {
      if (!pressedTimeoutRef.current) {
        pressedTimeoutRef.current = setTimeout(() => {
          pressedTimeoutRef.current = null;
        }, 250);
        onPress?.(event);
      }
    },
    [onPress],
  );
  React.useEffect(() => {
    if (
      Platform.OS === 'web' &&
      focusOnRender &&
      focusable &&
      pressableRef.current
    ) {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
      focusTimeoutRef.current = setTimeout(() => {
        pressableRef.current?.focus();
        hasFocusedOnRef.current = true;
        focusTimeoutRef.current = null;
      }, 1);
    }
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
        focusTimeoutRef.current = null;
      }
    };
  }, [focusOnRender, focusable, pressableRef]);

  React.useEffect(() => {
    const viewRefCurrent = pressableRef.current;
    if (Platform.OS === 'web' && viewRefCurrent && onContextPress) {
      (viewRefCurrent as unknown as HTMLElement).addEventListener(
        'contextmenu',
        onContextPress,
      );
    }
    return () => {
      if (Platform.OS === 'web' && viewRefCurrent && onContextPress) {
        (viewRefCurrent as unknown as HTMLElement).removeEventListener(
          'contextmenu',
          onContextPress,
        );
      }
    };
  }, [onContextPress, pressableRef]);

  React.useEffect(() => {
    return () => {
      if (pressedTimeoutRef.current) {
        clearTimeout(pressedTimeoutRef.current);
        pressedTimeoutRef.current = null;
      }
    };
  }, []);
  // A pressable without onPress isn't a pressable - skip wrapping the component in it
  if (!onPress && !onLongPress && !onPressIn) {
    return (
      <View
        style={[style, pointerEvents ? { pointerEvents } : undefined]}
        ref={pressableRef}
      >
        {children}
      </View>
    );
  }

  const finalAccessibilityLabel =
    Platform.OS === 'web'
      ? !!accessibilityLabel && !!accessibilityHint
        ? `${appendPunctuation(accessibilityLabel)} ${accessibilityHint}`
        : `${accessibilityLabel || ''}${accessibilityHint || ''}`
      : accessibilityLabel;

  return (
    <RNPressableTyped
      accessible={accessible}
      accessibilityLabel={finalAccessibilityLabel}
      accessibilityHint={accessibilityHint}
      ref={pressableRef}
      focusable={focusable}
      tabIndex={tabIndex}
      style={({
        pressed,
        hovered,
      }: {
        pressed: boolean;
        hovered?: boolean;
      }) => [
        style,
        hovered && onPress && !disabled ? hoveredStyle : undefined,
        pressed && onPress && !disabled ? pressedStyle : undefined,
        disabled
          ? styles.disabled
          : onPress
            ? styles.pressablePressable
            : undefined,
        pointerEvents ? { pointerEvents } : undefined,
      ]}
      onPressIn={onPressIn}
      onPress={onPress ? handlePress : undefined}
      onLongPress={onLongPress}
      onPressOut={onPressOut}
      onMoveShouldSetResponder={onMoveShouldSetResponder}
      onResponderGrant={onResponderGrant}
      onResponderMove={onResponderMove}
      onResponderRelease={onResponderRelease}
      onStartShouldSetResponder={onStartShouldSetResponder}
      onResponderTerminationRequest={onResponderTerminationRequest}
      onResponderTerminate={onResponderTerminate}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      hitSlop={4}
      disabled={disabled}
    >
      {menuElement}
      {children}
    </RNPressableTyped>
  );
};

const styles = StyleSheet.create({
  pressablePressable: {
    display: 'flex',
    ...Platform.select({
      native: {},
      web: {
        cursor: 'pointer',
        outlineOffset: -3,
      },
    }),
  },
  disabled: {
    opacity: 0.42,
  },
});
